import { useCallback, useMemo, useState } from 'react';
import { ColumnState, FilterChangedEvent, FilterModel, SortChangedEvent } from 'ag-grid-community';

import { useGetPaginatedMonitoring } from '@/api/monitoring/monitoring.hooks';
import { mapMonitoredEntitiesToRows } from '@/views/DashboardView/utils/mapMonitoredEntities';
import {
  readDashboardTableSetup,
  writeDashboardTableSetup,
} from '@/views/DashboardView/components/EntitiesSection/components/EntitiesTable/utils/dashboardTableSetup';
import {
  IGetPaginatedMonitoringRequest,
  IGetPaginatedMonitoringResponse,
} from '@/api/monitoring/monitoring.types';

const COL_ID_TO_SORT_BY: Record<string, string> = {
  displayName: 'display_name',
  pd: 'pd',
  type: 'type',
  severity: 'daily_severity',
  timeSinceAlert: 'latest_important_anomaly_timestamp',
  observableBalance: 'latest_balance',
};

export const FIRST_PAGE = 1;
export const DEFAULT_PAGE_SIZE = 25;
export const PAGE_SIZE_OPTIONS = [25, 50, 100];

const DISPLAY_NAME_COL_ID = 'displayName';
const TYPE_COL_ID = 'type';

const readDisplayNameFilter = (filterModel: FilterModel = {}) => {
  const filter: unknown = filterModel[DISPLAY_NAME_COL_ID]?.filter;

  return typeof filter === 'string' && !!filter.length ? filter : null;
};

// AgGridTypeCheckboxFilter keeps its model as a list of raw entity types
const readEntityTypesFilter = (filterModel: FilterModel = {}) => {
  const model: unknown = filterModel[TYPE_COL_ID];

  return Array.isArray(model) && !!model.length ? model.join(',') : null;
};

const readSort = (columnState: ColumnState[] = []) => {
  const sortedColumn = columnState.find(({ sort, colId }) => !!sort && !!COL_ID_TO_SORT_BY[colId]);

  if (!sortedColumn?.sort) return { sortBy: null, sortOrder: undefined };

  return {
    sortBy: COL_ID_TO_SORT_BY[sortedColumn.colId],
    sortOrder: sortedColumn.sort,
  };
};

const selectRows = (data: IGetPaginatedMonitoringResponse) => ({
  rows: mapMonitoredEntitiesToRows(data.items),
  total: data.total,
  pages: data.pages,
});

export const useDashboardTableQuery = () => {
  const [request, setRequest] = useState<IGetPaginatedMonitoringRequest>(() => {
    const { filterModel, columnState, page, size } = readDashboardTableSetup();

    return {
      ...readSort(columnState),
      displayName: readDisplayNameFilter(filterModel),
      entityTypes: readEntityTypesFilter(filterModel),
      allExtreme: null,
      page: page && page >= FIRST_PAGE ? page : FIRST_PAGE,
      size: size && PAGE_SIZE_OPTIONS.includes(size) ? size : DEFAULT_PAGE_SIZE,
    };
  });

  const { fetchedPaginatedMonitoredEntities, isLoading, isFetching, isPlaceholderData, error } =
    useGetPaginatedMonitoring(request, selectRows);

  const goToPage = useCallback(
    (page: number) => {
      if (request.page === page) return;

      setRequest(prev => ({ ...prev, page }));
      writeDashboardTableSetup({ page });
    },
    [request.page],
  );

  const changePageSize = useCallback(
    (size: number) => {
      if (request.size === size) return;

      setRequest(prev => ({ ...prev, size, page: FIRST_PAGE }));
      writeDashboardTableSetup({ size, page: FIRST_PAGE });
    },
    [request.size],
  );

  const handleSortChanged = useCallback(
    ({ api }: SortChangedEvent) => {
      const { sortBy, sortOrder } = readSort(api.getColumnState());

      if (request.sortBy === sortBy && request.sortOrder === sortOrder) return;

      setRequest(prev => ({ ...prev, sortBy, sortOrder, page: FIRST_PAGE }));
      writeDashboardTableSetup({ page: FIRST_PAGE });
    },
    [request.sortBy, request.sortOrder],
  );

  const handleFilterChanged = useCallback(
    ({ api }: FilterChangedEvent) => {
      const filterModel = api.getFilterModel();
      const displayName = readDisplayNameFilter(filterModel);
      const entityTypes = readEntityTypesFilter(filterModel);

      if (request.displayName === displayName && request.entityTypes === entityTypes) return;

      setRequest(prev => ({ ...prev, displayName, entityTypes, page: FIRST_PAGE }));
      writeDashboardTableSetup({ page: FIRST_PAGE });
    },
    [request.displayName, request.entityTypes],
  );

  const enableAlertFilter = useCallback(() => {
    setRequest(prev => ({ ...prev, allExtreme: true, page: FIRST_PAGE }));
    writeDashboardTableSetup({ page: FIRST_PAGE });
  }, []);

  const clearAlertFilter = useCallback(() => {
    setRequest(prev => ({ ...prev, allExtreme: null, page: FIRST_PAGE }));
    writeDashboardTableSetup({ page: FIRST_PAGE });
  }, []);

  const serverSideControls = useMemo(
    () => ({
      page: request.page,
      size: request.size,
      total: fetchedPaginatedMonitoredEntities?.total ?? 0,
      pages: fetchedPaginatedMonitoredEntities?.pages ?? 0,

      isFetchingNewData: isFetching && isPlaceholderData,
      isAlertFilterActive: request.allExtreme === true,
      goToPage,
      changePageSize,
      handleSortChanged,
      handleFilterChanged,
      enableAlertFilter,
      clearAlertFilter,

      request,
    }),
    [
      request,
      fetchedPaginatedMonitoredEntities,
      isFetching,
      isPlaceholderData,
      goToPage,
      changePageSize,
      handleSortChanged,
      handleFilterChanged,
      enableAlertFilter,
      clearAlertFilter,
    ],
  );

  return {
    rows: fetchedPaginatedMonitoredEntities?.rows ?? [],
    isDataReady: !!fetchedPaginatedMonitoredEntities,
    isLoading,
    error,
    serverSideControls,
  };
};

export type IServerSideTableControls = ReturnType<
  typeof useDashboardTableQuery
>['serverSideControls'];
